import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { getCatalogs } from '../firestore/Catalog'
import { createSlideDeck, getSlideDecks } from '../firestore/SlideDeck'
import { Catalog } from '../models/Catalog'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SlideDeck, SlideDeckStatus } from '../models/SlideDeck'
import { SlideDeckGroup } from '../stores/SlideDeckGroup'
import type { StaticModelCollection } from '../types'
import { Cubit } from './core'
import { SettingsSlideDeckTag } from '../models/SettingsSlideDeckTag'
import {
  getSettingsSlideDeckTags,
  hideSettingsSlideDeckTag,
  saveSettingsSlideDeckTags,
} from '../firestore/SettingsSlideDeckTag'
import { httpsCallable } from 'firebase/functions'
import { captureException } from '@sentry/core'
import { FirebaseError } from 'firebase/app'
import {
  addUserProfileFavoriteSlideDeck,
  getUserProfileFavoriteSlideDecks,
  removeUserProfileFavoriteSlideDeck,
} from '../firestore/UserProfileFavoriteSlideDeck'
import type { UserProfileFavoriteSlideDeck } from '../models/UserProfileFavoriteSlideDeck'
import { UserProfileFavoriteSlideDeck as UserProfileFavoriteSlideDeckModel } from '../models/UserProfileFavoriteSlideDeck'
import { SlideDeckType } from '../types'
import { guardOn, guardOnLoaded, type LoadingState } from '../util/guardOn'
import {
  fetchFeedbackAggregateForSlideDecksById,
  fetchFeedbackAggregateForSlideDecksByTypeId,
} from '../firestore/SlideDeck'

export const experienceTabs = [
  'all',
  'published',
  'drafts',
  'favorites',
  'archived',
  'demos',
  'globalAssets',
  'globalAuthors',
] as const

export type ExperienceTab = (typeof experienceTabs)[number]

export function validateAsExperienceTab(
  value: unknown
): ExperienceTab | undefined {
  if (
    typeof value === 'string' &&
    experienceTabs.includes(value as ExperienceTab)
  ) {
    return value as ExperienceTab
  }
  return undefined
}

export type ExperienceSortStrategy =
  | 'name'
  | 'lastUpdated'
  | 'experienceType'
  | 'catalogs'

export type FilteredSlideDeckGroup = {
  entireGroup: SlideDeckGroup
  entireGroupForCurrentTab: SlideDeck[]
  filteredSlideDecks: SlideDeck[]
}

export class AdminLibraryCubit extends Cubit {
  repository: FirebaseRepository

  slideDecks: StaticModelCollection<SlideDeck>
  catalogs: StaticModelCollection<Catalog>
  @observable searchPills: string[] = []

  private _settingsSlideDeckTags: StaticModelCollection<SettingsSlideDeckTag>

  @observable sortingStrategy: ExperienceSortStrategy = 'lastUpdated'
  @observable sortingOrder: 'asc' | 'desc' = 'desc'

  @observable slideDeckTypeFilter: SlideDeckType | 'all' = 'all'
  @observable filterForEntireGroups = false

  favoriteSlideDecks: StaticModelCollection<UserProfileFavoriteSlideDeck>
  @observable favoritingSlideDeckIds = observable.set<string>()
  @observable favoritesToRemember: string[] = []

  @observable feedbackAggregateByDeckId: Map<
    string,
    { totalScore: number; totalFeedback: number }
  > = new Map()

  @observable feedbackAggregateByTypeId: Map<
    string,
    {
      totalScore: number
      totalFeedback: number
      totalScoreRecent: number
      totalFeedbackRecent: number
    }
  > = new Map()

  @observable feedbackAggregatesLoaded = false

  constructor(
    repository: FirebaseRepository,
    options: {
      searchPills?: string[]
    } = {}
  ) {
    super()
    makeObservable(this)

    if (options.searchPills) {
      this.searchPills = options.searchPills
    }

    this.repository = repository
    this.slideDecks = SlideDeck.emptyCollection(repository)
    this.catalogs = Catalog.emptyCollection(repository)
    this._settingsSlideDeckTags =
      SettingsSlideDeckTag.emptyCollection(repository)
    this.favoriteSlideDecks =
      UserProfileFavoriteSlideDeckModel.emptyCollection(repository)
  }

  initialize(): void {
    if (!this.repository.currentUser) {
      return
    }
    this.addStream(
      getUserProfileFavoriteSlideDecks(this.repository, {
        userId: this.repository.currentUser.uid,
      }),
      (favoriteSlideDecks) => {
        runInAction(() => {
          this.favoriteSlideDecks.replaceModels(favoriteSlideDecks)
        })
      }
    )
    this.addStream(getSlideDecks(this.repository), (rooms) => {
      this.slideDecks.replaceModels(rooms)
    })
    this.addStream(getCatalogs(this.repository), (catalogs) => {
      this.catalogs.replaceModels(catalogs)
    })
    Promise.all([
      fetchFeedbackAggregateForSlideDecksById(this.repository),
      fetchFeedbackAggregateForSlideDecksByTypeId(this.repository),
    ])
      .then(([byId, byTypeId]) => {
        runInAction(() => {
          this.feedbackAggregateByDeckId = new Map(Object.entries(byId))
          this.feedbackAggregateByTypeId = new Map(Object.entries(byTypeId))
          this.feedbackAggregatesLoaded = true
        })
      })
      .catch(() => {})
  }

  @action
  setSearchPills(pills: string[]) {
    this.searchPills = pills
    this.clearFavoritesToRemember()
  }

  @action
  setSlideDeckTypeFilter(filter: SlideDeckType | 'all') {
    this.slideDeckTypeFilter = filter
    this.clearFavoritesToRemember()
  }

  @action
  setFilterForEntireGroups(flag: boolean) {
    this.filterForEntireGroups = flag
    this.clearFavoritesToRemember()
  }

  @action
  addToFavoritesToRemember(deckId: string) {
    if (!this.favoritesToRemember.includes(deckId)) {
      this.favoritesToRemember.push(deckId)
    }
  }

  @action
  clearFavoritesToRemember() {
    this.favoritesToRemember = []
  }

  /**
   * Called by the component via useEffect whenever the URL tab changes.
   * Handles side effects that used to live in `setSelectedTab`.
   */
  @action
  onTabChanged(tab: ExperienceTab): void {
    this.clearFavoritesToRemember()
    if (tab === 'demos') {
      this.slideDeckTypeFilter = SlideDeckType.demo
    } else if (this.slideDeckTypeFilter === SlideDeckType.demo) {
      this.slideDeckTypeFilter = 'all'
    }
  }

  @action
  changeSortingStrategy = (
    strategy: ExperienceSortStrategy,
    order: 'asc' | 'desc'
  ) => {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  private applySearchFilter({
    slideDecks,
    entireGroup,
  }: {
    slideDecks: SlideDeck[]
    entireGroup: SlideDeckGroup
  }): SlideDeck[] {
    if (this.searchPills.length === 0) {
      return slideDecks
    }

    return slideDecks.filter((deck) => {
      const name = deck.data.slideDeckName.toLowerCase()
      const teaser = deck.data.slideDeckTeaser.toLowerCase()
      const version = deck.data.slideDeckVersion.toLowerCase()
      const mainDeckName = entireGroup.name.toLowerCase()
      const mainDeckTeaser = entireGroup.teaser.toLowerCase()
      const mainDeckVersion = entireGroup.version.toLowerCase()
      const deckId = deck.id.toLowerCase()

      return this.searchPills.some(
        (pill) =>
          name.includes(pill.toLowerCase()) ||
          teaser.includes(pill.toLowerCase()) ||
          version.includes(pill.toLowerCase()) ||
          mainDeckName.includes(pill.toLowerCase()) ||
          mainDeckTeaser.includes(pill.toLowerCase()) ||
          mainDeckVersion.includes(pill.toLowerCase()) ||
          deckId === pill.toLowerCase()
      )
    })
  }

  private applyTabFilter({
    slideDecks,
    favoriteSlideDecks,
    tab,
  }: {
    slideDecks: SlideDeck[]
    favoriteSlideDecks: UserProfileFavoriteSlideDeck[]
    tab: ExperienceTab
  }): SlideDeck[] {
    const isArchived = (deck: SlideDeck) => deck.isArchived

    switch (tab) {
      case 'archived':
        return slideDecks.filter(isArchived)
      case 'all':
        return slideDecks.filter((deck) => !isArchived(deck))
      case 'published':
        return slideDecks.filter(
          (deck) => deck.slideDeckStatus === SlideDeckStatus.published
        )
      case 'drafts':
        return slideDecks.filter(
          (deck) => deck.slideDeckStatus === SlideDeckStatus.draft
        )
      case 'favorites':
        return slideDecks.filter(
          (deck) =>
            favoriteSlideDecks.some((f) => f.id === deck.id) ||
            this.favoritesToRemember.includes(deck.id)
        )
      case 'demos':
        return slideDecks.filter((deck) => !isArchived(deck))
      case 'globalAssets':
        return []
      case 'globalAuthors':
        return []
      default:
        return slideDecks
    }
  }

  private applyTypeFilter(slideDecks: SlideDeck[]): SlideDeck[] {
    if (this.slideDeckTypeFilter === 'all') {
      return slideDecks
    }

    return slideDecks.filter(
      (deck) => deck.data.slideDeckType === this.slideDeckTypeFilter
    )
  }

  /**
   * Expensive group+sort work cached as a `@computed`. Does NOT depend on
   * the tab — tab-specific filtering happens in `getAllSlideDecksGrouped`
   * as a cheap transform on top of this.
   */
  @computed
  private get sortedGroups(): LoadingState<SlideDeckGroup[]> {
    return guardOn(
      this.slideDecks.isLoaded &&
        this.slideDecks.models.every((deck) => deck.isLoaded) &&
        this.favoriteSlideDecks.isLoaded,
      () => {
        const grouped = new Map<string, SlideDeckGroup>()

        this.slideDecks.models.forEach((slideDeck) => {
          const parentId = slideDeck.data.slideDeckTypeId || slideDeck.id
          let group = grouped.get(parentId)

          if (!group) {
            group = new SlideDeckGroup(parentId, true)
            grouped.set(parentId, group)
          }

          group.addSlideDeck(slideDeck)
        })

        const groups = Array.from(grouped.values())

        return groups.sort((a, b) => {
          let compare = 0
          switch (this.sortingStrategy) {
            case 'name':
              compare = a.name.localeCompare(b.name)
              break
            case 'lastUpdated':
              compare = a.updatedAt.getTime() - b.updatedAt.getTime()
              break
            case 'experienceType': {
              const getDisplayString = (group: SlideDeckGroup): string => {
                const uniqueTypes = Array.from(
                  new Set(group.slideDecks.map((d) => d.data.slideDeckType))
                ).filter((type) => type !== undefined)
                if (uniqueTypes.length === 0) return '-'
                if (uniqueTypes.length === 1) {
                  if (uniqueTypes[0] === SlideDeckType.original) {
                    return 'Breakout Original'
                  }
                  const typeKey = SlideDeckType[uniqueTypes[0]] as string
                  return typeKey
                }
                return 'Multiple'
              }
              const aDisplay = getDisplayString(a)
              const bDisplay = getDisplayString(b)
              compare = aDisplay.localeCompare(bDisplay)
              break
            }
            case 'catalogs': {
              const aCatalogs = new Set(
                a.slideDecks.flatMap((deck) => deck.data.catalogIds || [])
              ).size
              const bCatalogs = new Set(
                b.slideDecks.flatMap((deck) => deck.data.catalogIds || [])
              ).size
              compare = aCatalogs - bCatalogs
              break
            }
          }
          return this.sortingOrder === 'asc' ? compare : -compare
        })
      }
    )
  }

  getAllSlideDecksGrouped(
    tab: ExperienceTab
  ): LoadingState<FilteredSlideDeckGroup[]> {
    return guardOnLoaded(this.sortedGroups, (sortedGroups) =>
      sortedGroups.map((group) => ({
        entireGroup: group,
        entireGroupForCurrentTab: group.slideDecks.filter(
          (deck) =>
            tab === 'archived' || tab === 'favorites' || !deck.isArchived
        ),
        filteredSlideDecks: group.slideDecks,
      }))
    )
  }

  getSlideDeckGroupsTabbed(tab: ExperienceTab) {
    return guardOnLoaded(this.getAllSlideDecksGrouped(tab), (groups) => {
      return groups
        .map((group) => ({
          ...group,
          filteredSlideDecks: this.applyTabFilter({
            slideDecks: group.filteredSlideDecks,
            favoriteSlideDecks: this.favoriteSlideDecks.models,
            tab,
          }),
        }))
        .filter((group) => group.filteredSlideDecks.length > 0)
    })
  }

  /**
   * Search-filtered groups with no tab dependency. Cached as a `@computed`
   * so the 6 tab-count methods don't each re-run the search filter.
   */
  @computed
  private get searchedGroups(): LoadingState<
    { entireGroup: SlideDeckGroup; filteredSlideDecks: SlideDeck[] }[]
  > {
    return guardOnLoaded(this.sortedGroups, (sortedGroups) =>
      sortedGroups
        .map((group) => ({
          entireGroup: group,
          filteredSlideDecks: this.applySearchFilter({
            slideDecks: group.slideDecks,
            entireGroup: group,
          }),
        }))
        .filter((group) => group.filteredSlideDecks.length > 0)
    )
  }

  getSlideDeckGroupsSearched(
    tab: ExperienceTab
  ): LoadingState<FilteredSlideDeckGroup[]> {
    return guardOnLoaded(this.searchedGroups, (groups) =>
      groups.map((group) => ({
        ...group,
        entireGroupForCurrentTab: group.entireGroup.slideDecks.filter(
          (deck) =>
            tab === 'archived' || tab === 'favorites' || !deck.isArchived
        ),
      }))
    )
  }

  getSlideDeckGroupsSearchedTabbed(tab: ExperienceTab) {
    return guardOnLoaded(this.getAllSlideDecksGrouped(tab), (groups) => {
      return groups
        .map((group) => {
          const searched = this.applySearchFilter({
            slideDecks: group.filteredSlideDecks,
            entireGroup: group.entireGroup,
          })
          const tabbed = this.applyTabFilter({
            slideDecks: searched,
            favoriteSlideDecks: this.favoriteSlideDecks.models,
            tab,
          })
          return {
            ...group,
            filteredSlideDecks: tabbed,
          }
        })
        .filter((group) => group.filteredSlideDecks.length > 0)
    })
  }

  getSlideDeckGroupsFiltered(
    tab: ExperienceTab
  ): LoadingState<FilteredSlideDeckGroup[]> {
    return guardOnLoaded(this.getAllSlideDecksGrouped(tab), (groups) => {
      return groups
        .map((group) => {
          const searched = this.applySearchFilter({
            slideDecks: group.filteredSlideDecks,
            entireGroup: group.entireGroup,
          })
          const tabbed = this.applyTabFilter({
            slideDecks: searched,
            favoriteSlideDecks: this.favoriteSlideDecks.models,
            tab,
          })
          const typed = this.applyTypeFilter(tabbed)
          return {
            ...group,
            filteredSlideDecks: typed,
          }
        })
        .filter((group) => group.filteredSlideDecks.length > 0)
    })
  }

  // Tab counts are independent of the active tab — they report how many
  // groups would match each tab's criteria. Cached as @computed on top of
  // the shared `searchedGroups` so toggling the active tab is free.
  @computed
  get allTabCount() {
    return guardOnLoaded(
      this.searchedGroups,
      (slideDecksGroups) =>
        slideDecksGroups.filter((group) =>
          group.filteredSlideDecks.some((deck) => !deck.isArchived)
        ).length
    )
  }

  @computed
  get publishedTabCount() {
    return guardOnLoaded(
      this.searchedGroups,
      (slideDecksGroups) =>
        slideDecksGroups.filter((group) =>
          group.filteredSlideDecks.some(
            (slideDeck: SlideDeck) =>
              slideDeck.slideDeckStatus === SlideDeckStatus.published
          )
        ).length
    )
  }

  @computed
  get draftTabCount() {
    return guardOnLoaded(
      this.searchedGroups,
      (slideDecksGroups) =>
        slideDecksGroups.filter((group) =>
          group.filteredSlideDecks.some(
            (slideDeck: SlideDeck) =>
              slideDeck.slideDeckStatus === SlideDeckStatus.draft
          )
        ).length
    )
  }

  @computed
  get demoTabCount() {
    return guardOnLoaded(
      this.searchedGroups,
      (slideDecksGroups) =>
        slideDecksGroups.filter((group) =>
          group.filteredSlideDecks.some(
            (deck) =>
              deck.data.slideDeckType === SlideDeckType.demo && !deck.isArchived
          )
        ).length
    )
  }

  @computed
  get archivedTabCount() {
    return guardOnLoaded(
      this.searchedGroups,
      (slideDecksGroups) =>
        slideDecksGroups.filter((group) =>
          group.filteredSlideDecks.some((deck) => deck.isArchived)
        ).length
    )
  }

  @computed
  get favoriteTabCount() {
    return guardOnLoaded(
      this.searchedGroups,
      (groups) =>
        groups.filter((group) =>
          group.filteredSlideDecks.some((deck) =>
            this.favoriteSlideDecks.models.some((f) => f.id === deck.id)
          )
        ).length
    )
  }

  @computed
  get settingsSlideDeckTags() {
    const streamKey = 'settingsSlideDeckTags'
    if (!this.hasStream(streamKey)) {
      this.addStream(
        getSettingsSlideDeckTags(this.repository),
        (settingsSlideDeckTags) => {
          this.settingsSlideDeckTags.replaceModels(settingsSlideDeckTags)
        },
        {
          name: streamKey,
        }
      )
    }
    return this._settingsSlideDeckTags
  }

  /**
   * Add a new slide deck to the library
   *
   * @returns string - The ID of the newly created slide deck
   */
  addSlideDeck(): Promise<string> {
    return createSlideDeck(this.repository)
  }

  async addFavoriteSlideDeck(slideDeck: SlideDeck): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    const slideDeckId = slideDeck.id
    runInAction(() => {
      this.favoritingSlideDeckIds.add(slideDeckId)
    })
    try {
      await addUserProfileFavoriteSlideDeck(this.repository, {
        userId,
        slideDeckId: slideDeckId,
        slideDeckTypeId: slideDeck.data.slideDeckTypeId,
      })
    } finally {
      runInAction(() => {
        this.favoritingSlideDeckIds.delete(slideDeckId)
      })
    }
  }

  async removeFavoriteSlideDeck(slideDeck: SlideDeck): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    const slideDeckId = slideDeck.id
    runInAction(() => {
      this.favoritingSlideDeckIds.add(slideDeckId)
    })
    try {
      await removeUserProfileFavoriteSlideDeck(this.repository, {
        userId,
        slideDeckId: slideDeckId,
      })
    } finally {
      runInAction(() => {
        this.favoritingSlideDeckIds.delete(slideDeckId)
      })
    }
  }

  saveSettingsSlideDeckTags = async (
    params: Parameters<typeof saveSettingsSlideDeckTags>[1]
  ) => {
    return await saveSettingsSlideDeckTags(this.repository, {
      ...params,
    })
  }

  hideSettingsSlideDeckTag = async (
    params: Parameters<typeof hideSettingsSlideDeckTag>[1]
  ) => {
    return await hideSettingsSlideDeckTag(this.repository, {
      ...params,
    })
  }

  triggerEmailTemplateRun = async (
    kind:
      | 'dailySessionReminder'
      | 'roomStateMessage'
      | 'sessionSoonReminder'
      | 'sessionResultsAvailable'
  ) => {
    const callFunc = httpsCallable<string, Record<string, unknown>[]>(
      this.repository.functions,
      'invokeEmailTemplateRunForSuperAdminV1'
    )
    try {
      const { data } = await callFunc(kind)

      return {
        success: true,
        message: data.length
          ? `${data.length} emails sent`
          : `No emails to send`,
        results: data,
      }
    } catch (e) {
      captureException(e)
      const msg = e instanceof FirebaseError ? e.message : e
      return {
        success: false,
        message: `Error invoking email template run:, ${msg}`,
        results: [],
      }
    }
  }

  getFeedbackAggregateForDeck(deckId: string) {
    return guardOn(this.feedbackAggregatesLoaded, () =>
      this.feedbackAggregateByDeckId.get(deckId)
    )
  }

  getFeedbackAggregateForTypeId(typeId: string) {
    return guardOn(this.feedbackAggregatesLoaded, () =>
      this.feedbackAggregateByTypeId.get(typeId)
    )
  }
}
